import { GameState } from "./state";
import { UPGRADES, type UpgradeDef } from "../data/upgrades";
import { maxIntegrity } from "./stats";

// Upgrade stacks are tracked per-run on GameState (s.upgradeStacks), so they
// reset automatically with a new run and can be shown in the HUD.
function countOf(s: GameState, id: string): number {
  return s.upgradeStacks.find((e) => e.id === id)?.count ?? 0;
}

/** Roll n distinct upgrade choices, weighted, respecting stack & level gates. */
export function rollChoices(s: GameState, n = 3): UpgradeDef[] {
  const pool = UPGRADES.filter(
    (u) =>
      (u.maxStacks === 0 || countOf(s, u.id) < u.maxStacks) &&
      (u.minLevel === undefined || s.level >= u.minLevel),
  );

  const chosen: UpgradeDef[] = [];
  const working = [...pool];
  while (chosen.length < n && working.length > 0) {
    const total = working.reduce((a, u) => a + u.weight, 0);
    let r = s.rng.next() * total;
    let idx = 0;
    for (let i = 0; i < working.length; i++) {
      r -= working[i].weight;
      if (r <= 0) {
        idx = i;
        break;
      }
    }
    chosen.push(working[idx]);
    working.splice(idx, 1);
  }
  return chosen;
}

export function applyUpgrade(s: GameState, u: UpgradeDef): void {
  const before = maxIntegrity(s.stats);
  u.apply(s.stats);

  const entry = s.upgradeStacks.find((e) => e.id === u.id);
  if (entry) entry.count++;
  else s.upgradeStacks.push({ id: u.id, name: u.name, color: u.color, icon: u.icon, count: 1 });

  // Max-integrity upgrades also top the player up by the gained amount.
  const after = maxIntegrity(s.stats);
  if (after > before) {
    s.player.integrity = Math.min(after, s.player.integrity + (after - before));
  }
  s.ring(s.player.x, s.player.y, u.color, 22, 260);
}
